/** Backend uses these labels for missing scrape or detection. */
const GENERIC_NORMALIZED = new Set([
  "unknown",
  "unknown company",
  "role not detected",
  "not specified",
]);

export function isGenericAuditSegment(segment) {
  const n = (segment || "").trim().toLowerCase();
  return GENERIC_NORMALIZED.has(n);
}

export function normalizeAuditSegmentLabel(segment) {
  const cleaned = (segment || "")
    .replace(/^other:\s*/i, "")
    .replace(/[_]+/g, " ")
    .replace(/\s*-\s*/g, "-")
    .replace(/\s+/g, " ")
    .trim();
  if (!cleaned) return "";

  return cleaned
    .split(" ")
    .map((word) =>
      word
        .split("-")
        .map((part) => {
          if (!part) return part;
          const lower = part.toLowerCase();
          if (lower === "b2b" || lower === "b2c" || lower === "ugc") return lower.toUpperCase();
          if (lower === "vp" || lower === "cto" || lower === "ceo" || lower === "cmo") return lower.toUpperCase();
          return `${lower.charAt(0).toUpperCase()}${lower.slice(1)}`;
        })
        .join("-")
    )
    .join(" ");
}

function weightedAverage(total, count) {
  return count > 0 ? Math.round((10 * total) / count) / 10 : null;
}

export function aggregateAuditSegments(rows) {
  const buckets = new Map();
  for (const row of rows || []) {
    const segment = normalizeAuditSegmentLabel(row.segment);
    const key = segment.toLowerCase();
    const count = row.count ?? 0;
    const scoredCount = row.scored_count ?? 0;
    const matchScoreCount = row.avg_match_score == null ? 0 : scoredCount;
    const companyScoreCount = row.avg_company_match_score == null ? 0 : scoredCount;
    const roleScoreCount = row.avg_role_match_score == null ? 0 : scoredCount;
    const prev = buckets.get(key) || {
      ...row,
      segment,
      count: 0,
      scored_count: 0,
      icp_match_count: 0,
      avgMatchTotal: 0,
      avgMatchCount: 0,
      avgCompanyTotal: 0,
      avgCompanyCount: 0,
      avgRoleTotal: 0,
      avgRoleCount: 0,
    };

    buckets.set(key, {
      ...prev,
      count: prev.count + count,
      scored_count: prev.scored_count + scoredCount,
      icp_match_count: prev.icp_match_count + (row.icp_match_count ?? 0),
      avgMatchTotal: prev.avgMatchTotal + (row.avg_match_score ?? 0) * matchScoreCount,
      avgMatchCount: prev.avgMatchCount + matchScoreCount,
      avgCompanyTotal: prev.avgCompanyTotal + (row.avg_company_match_score ?? 0) * companyScoreCount,
      avgCompanyCount: prev.avgCompanyCount + companyScoreCount,
      avgRoleTotal: prev.avgRoleTotal + (row.avg_role_match_score ?? 0) * roleScoreCount,
      avgRoleCount: prev.avgRoleCount + roleScoreCount,
    });
  }

  const totalRows = [...buckets.values()].reduce((sum, row) => sum + row.count, 0);
  return [...buckets.values()]
    .map((row) => ({
      ...row,
      share_pct: totalRows ? Math.round((1000 * row.count) / totalRows) / 10 : 0,
      avg_match_score: weightedAverage(row.avgMatchTotal, row.avgMatchCount),
      avg_company_match_score: weightedAverage(row.avgCompanyTotal, row.avgCompanyCount),
      avg_role_match_score: weightedAverage(row.avgRoleTotal, row.avgRoleCount),
      icp_match_rate_pct: row.count ? Math.round((1000 * row.icp_match_count) / row.count) / 10 : 0,
    }))
    .sort((a, b) => {
      const countDelta = (b.count ?? 0) - (a.count ?? 0);
      if (countDelta !== 0) return countDelta;
      return a.segment.localeCompare(b.segment);
    });
}

/** Split API bucket rows into generic vs labeled; preserves sort within each group. */
export function partitionAuditSegments(rows) {
  const generic = [];
  const labeled = [];
  for (const row of rows || []) {
    if (isGenericAuditSegment(row.segment)) generic.push(row);
    else labeled.push(row);
  }
  return { generic, labeled };
}

export function sumCounts(rows) {
  return (rows || []).reduce((a, r) => a + (r.count || 0), 0);
}
